import type {
  CompanyAnalysisListItem,
  CompanyPeriodListItem,
  ControlCompanyListItem,
} from "@/models/company.model";
import { create } from "zustand";

const RESET_FILTERS = {
  selectedCompanyId: null,
  selectedPeriodId: null,
  selectedAnalysisId: null,
};

type FilterOption = {
  value: string;
  label: string;
  badge?: string;
};

export function createFilterOptions<T>(
  items: T[],
  idKey: keyof T,
  labelKey: keyof T,
  badgeId?: string,
): FilterOption[] {
  return items.map((item) => ({
    value: String(item[idKey]),
    label: String(item[labelKey]),
    badge: item[idKey] === badgeId ? "latest_tag" : undefined,
  }));
}

function extractAnalysesFromCompanies(
  companies: ControlCompanyListItem[],
): CompanyAnalysisListItem[] {
  return companies.flatMap((company) =>
    company.periods.flatMap((period) => period.analysis),
  );
}

export function getLastCreatedAnalysis(
  analyses: CompanyAnalysisListItem[],
): CompanyAnalysisListItem {
  return analyses.sort((a, b) => {
    const aDate = new Date(a.createdAt);
    const bDate = new Date(b.createdAt);
    return bDate.getTime() - aDate.getTime();
  })[0];
}

function getCompanyFromAnalysisId(
  companies: ControlCompanyListItem[],
  analysisId: string,
): ControlCompanyListItem | null {
  return (
    companies.find((company) =>
      company.periods.some((period) =>
        period.analysis.some((analysis) => analysis.id === analysisId),
      ),
    ) ?? null
  );
}

function getPeriodFromAnalysisId(
  companies: ControlCompanyListItem[],
  analysisId: string,
): CompanyPeriodListItem | null {
  const company = getCompanyFromAnalysisId(companies, analysisId);
  return (
    company?.periods.find((period) =>
      period.analysis.some((analysis) => analysis.id === analysisId),
    ) ?? null
  );
}

function handleInitialAnalysisId(
  companies: ControlCompanyListItem[],
  initialAnalysisId: string,
) {
  const company = getCompanyFromAnalysisId(companies, initialAnalysisId);
  if (!company) {
    return null;
  }

  const period = getPeriodFromAnalysisId(companies, initialAnalysisId);
  if (!period) {
    return null;
  }

  const lastCreatedAnalysis = getLastCreatedAnalysis(period.analysis);

  return {
    selectedCompanyId: company.id,
    selectedPeriodId: period.id,
    selectedAnalysisId: initialAnalysisId,
    availableCompanies: createFilterOptions(companies, "id", "name"),
    availablePeriods: createFilterOptions(company.periods, "id", "name"),
    availableAnalyses: createFilterOptions(
      period.analysis,
      "id",
      "name",
      lastCreatedAnalysis.id,
    ),
  };
}

export function handleDefaultSelection(companies: ControlCompanyListItem[]) {
  if (companies.length === 0) {
    return null;
  }

  const analyses = extractAnalysesFromCompanies(companies);
  const lastCreatedAnalysis = getLastCreatedAnalysis(analyses);

  if (!lastCreatedAnalysis) {
    return null;
  }

  const company = getCompanyFromAnalysisId(companies, lastCreatedAnalysis.id);

  if (!company) {
    return null;
  }

  const period = getPeriodFromAnalysisId(companies, lastCreatedAnalysis.id);
  if (!period) {
    return null;
  }

  return {
    selectedAnalysisId: lastCreatedAnalysis.id,
    selectedCompanyId: company.id,
    selectedPeriodId: period.id,
    availableCompanies: createFilterOptions(companies, "id", "name"),
    availablePeriods: createFilterOptions(company.periods, "id", "name"),
    availableAnalyses: createFilterOptions(
      period.analysis,
      "id",
      "name",
      lastCreatedAnalysis.id,
    ),
  };
}

export function handlePeriodSelection(
  companies: ControlCompanyListItem[],
  selectedCompanyId: string | null,
  periodId: string,
) {
  if (!selectedCompanyId) {
    return null;
  }

  const company = companies.find((company) => company.id === selectedCompanyId);
  if (!company) {
    return null;
  }

  const period = company.periods.find((period) => period.id === periodId);
  if (!period) {
    return null;
  }

  // Handle case where period has no analyses
  if (!period.analysis || period.analysis.length === 0) {
    return null;
  }

  const lastCreatedAnalysis = getLastCreatedAnalysis(period.analysis);
  const availableAnalyses = createFilterOptions(
    period.analysis,
    "id",
    "name",
    lastCreatedAnalysis.id,
  );

  return {
    selectedPeriodId: periodId,
    selectedAnalysisId: lastCreatedAnalysis.id,
    availableAnalyses,
  };
}

export function handleCompanySelection(
  companies: ControlCompanyListItem[],
  companyId: string,
) {
  const company = companies.find((company) => company.id === companyId);
  if (!company) {
    return null;
  }

  // Handle case where company has no periods
  if (!company.periods || company.periods.length === 0) {
    return null;
  }

  const availablePeriods = createFilterOptions(company.periods, "id", "name");
  const analyses = company.periods.flatMap((period) => period.analysis);

  // Handle case where no analyses exist
  if (!analyses || analyses.length === 0) {
    return null;
  }

  const lastCreatedAnalysis = getLastCreatedAnalysis(analyses);

  // Find the period that contains the selected analysis
  const period = getPeriodFromAnalysisId(companies, lastCreatedAnalysis.id);
  if (!period) {
    return null;
  }

  // Use analyses from the selected period only, not all analyses
  const availableAnalyses = createFilterOptions(
    period.analysis,
    "id",
    "name",
    lastCreatedAnalysis.id,
  );

  return {
    selectedCompanyId: companyId,
    selectedAnalysisId: lastCreatedAnalysis.id,
    selectedPeriodId: period.id,
    availablePeriods,
    availableAnalyses,
  };
}

interface MainControlFiltersState {
  companies: ControlCompanyListItem[];
  selectedAnalysisId: string | null;
  selectedCompanyId: string | null;
  selectedPeriodId: string | null;
  availableCompanies: FilterOption[];
  availablePeriods: FilterOption[];
  availableAnalyses: FilterOption[];
  methods: {
    setSelectedAnalysisId: (analysisIds: string[]) => void;
    setSelectedCompanyId: (companyIds: string[]) => void;
    setSelectedPeriodId: (periodIds: string[]) => void;
    onCompaniesLoaded: (
      companies: ControlCompanyListItem[],
      initialAnalysisId: string | null,
    ) => void;
  };
}

export const useMainControlFiltersStore = create<MainControlFiltersState>(
  (set, get) => ({
    companies: [],
    selectedAnalysisId: null,
    selectedCompanyId: null,
    selectedPeriodId: null,
    availableCompanies: [],
    availablePeriods: [],
    availableAnalyses: [],
    methods: {
      setSelectedAnalysisId: (analysisIds) => {
        const analysisId = analysisIds[0] ?? null;
        set({ selectedAnalysisId: analysisId });
      },
      setSelectedCompanyId: (companyIds) => {
        const companyId = companyIds[0] ?? null;
        const companies = get().companies;

        if (!companyId) {
          return set(RESET_FILTERS);
        }

        const stateUpdate = handleCompanySelection(companies, companyId);

        if (!stateUpdate) {
          return set(RESET_FILTERS);
        }

        set(stateUpdate);
      },
      setSelectedPeriodId: (periodIds) => {
        const periodId = periodIds[0] ?? null;
        const companies = get().companies;
        const selectedCompanyId = get().selectedCompanyId;

        if (!periodId) {
          return set(RESET_FILTERS);
        }

        const stateUpdate = handlePeriodSelection(
          companies,
          selectedCompanyId,
          periodId,
        );

        if (!stateUpdate) {
          return set(RESET_FILTERS);
        }

        set(stateUpdate);
      },
      onCompaniesLoaded: (companies, initialAnalysisId) => {
        set({ companies });

        let stateUpdate = null;

        if (initialAnalysisId) {
          stateUpdate = handleInitialAnalysisId(companies, initialAnalysisId);
        }

        if (!stateUpdate) {
          stateUpdate = handleDefaultSelection(companies);
        }

        if (!stateUpdate) {
          return set(RESET_FILTERS);
        }

        set(stateUpdate);
      },
    },
  }),
);
